import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '#pplx/frontend/aether/components/Badge/index.js';
import { Modal } from '#pplx/frontend/aether/components/Modal/index.js';
import { BoxPrimitive } from '#pplx/frontend/aether/primitives/BoxPrimitive/index.js';
import { TextPrimitive } from '#pplx/frontend/aether/primitives/TextPrimitive/index.js';
import { useKeyboardHintStyle } from '#pplx/frontend/apps/ask/user-settings/hooks/useKeyboardHintStyle.js';
import { useWebPlatform } from '#pplx/frontend/platform-components/useWebPlatform.js';
import React from 'react';
import { defineMessage, useIntl } from 'react-intl';
/** Set of modifier-key symbols used to visually distinguish them from action keys. */
const MODIFIER_SYMBOLS = new Set(['⌘', '⌥', '⌃', '⇧', 'Ctrl', 'Alt', 'Shift', 'Cmd', 'Opt']);
const KeyBadge = ({ children }) => {
    const isModifier = MODIFIER_SYMBOLS.has(children);
    return (_jsx(BoxPrimitive, { display: "flex", align: "center", justify: "center", rounded: "md", className: isModifier
            ? 'border-super-subtle bg-super-subtle h-[26px] min-w-[26px] border px-1'
            : 'border-super-subtle bg-super-soft h-[26px] min-w-[26px] border px-1', children: _jsx(TextPrimitive, { variant: "extraSmall", color: isModifier ? 'light' : 'default', children: children }) }));
};
const ShortcutRow = ({ shortcut }) => {
    const { $t } = useIntl();
    return (_jsxs(BoxPrimitive, { display: "flex", align: "center", className: "group justify-between py-2", children: [_jsxs(BoxPrimitive, { display: "flex", align: "center", className: "min-w-0 gap-1", children: [_jsx(TextPrimitive, { as: "dd", variant: "small", color: "light", lineClamp: 1, className: "overflow-hidden text-clip", children: $t(shortcut.label) }), shortcut.mode && _jsx(Badge, { children: shortcut.mode })] }), _jsx(BoxPrimitive, { display: "flex", align: "center", as: "dl", className: "shrink-0 gap-1", children: shortcut.displayKeys.map((k) => (_jsx(KeyBadge, { children: k }, k))) })] }));
};
const SectionGroup = ({ section, isFirst, }) => {
    const { $t } = useIntl();
    return (_jsxs(BoxPrimitive, { className: isFirst ? '' : 'mt-8', children: [_jsx(TextPrimitive, { variant: "smallCaps", color: "ultraLight", className: "mb-0.5", children: $t(section.heading) }), _jsx(BoxPrimitive, { as: "dl", className: "divide-super-subtle divide-y", children: section.shortcuts.map((shortcut) => (_jsx(ShortcutRow, { shortcut: shortcut }, String(shortcut.label.id)))) })] }));
};
/** Mac modifier key legend: ⌘ Command, ⌥ Option, ⌃ Control, ⇧ Shift. */
const MAC_KEY_LEGEND_SYMBOLS = [
    {
        symbol: '⌘',
        label: defineMessage({
            defaultMessage: 'Command',
            description: 'Key legend label for the Command (⌘) modifier key',
            id: 'adbfcd6329',
        }),
    },
    {
        symbol: '⌥',
        label: defineMessage({
            defaultMessage: 'Option',
            description: 'Key legend label for the Option (⌥) modifier key',
            id: 'af4e674fd0',
        }),
    },
    {
        symbol: '⌃',
        label: defineMessage({
            defaultMessage: 'Control',
            description: 'Key legend label for the Control (⌃) modifier key',
            id: 'bd6668e5ef',
        }),
    },
    {
        symbol: '⇧',
        label: defineMessage({
            defaultMessage: 'Shift',
            description: 'Key legend label for the Shift (⇧) modifier key',
            id: '8a4e2e2b47',
        }),
    },
];
/** Non-Mac modifier key legend: Ctrl, Alt, Shift. */
const NON_MAC_KEY_LEGEND_SYMBOLS = [
    {
        symbol: 'Ctrl',
        label: defineMessage({
            defaultMessage: 'Control',
            description: 'Key legend label for the Ctrl modifier key on non-Mac platforms',
            id: '9cd7c453dd',
        }),
    },
    {
        symbol: 'Alt',
        label: defineMessage({
            defaultMessage: 'Alt',
            description: 'Key legend label for the Alt modifier key on non-Mac platforms',
            id: '5cbf7c3b9d',
        }),
    },
    {
        symbol: '⇧',
        label: defineMessage({
            defaultMessage: 'Shift',
            description: 'Key legend label for the Shift modifier key on non-Mac platforms',
            id: '6c63f3f7d0',
        }),
    },
];
const KeyLegend = () => {
    const { $t } = useIntl();
    const { device: { isMacOS }, } = useWebPlatform();
    const hintStyle = useKeyboardHintStyle();
    // In text mode the key names are self-explanatory, so the legend is unnecessary.
    if (hintStyle === 'text')
        return null;
    const legend = isMacOS ? MAC_KEY_LEGEND_SYMBOLS : NON_MAC_KEY_LEGEND_SYMBOLS;
    return (_jsx(BoxPrimitive, { display: "flex", align: "center", className: "flex-wrap gap-4", children: legend.map(({ symbol, label }) => (_jsxs(BoxPrimitive, { display: "flex", align: "center", className: "gap-1", children: [_jsx(KeyBadge, { children: symbol }), _jsx(TextPrimitive, { variant: "extraSmall", color: "light", children: $t(label) })] }, symbol))) }));
};
const MODAL_TITLE = defineMessage({
    defaultMessage: 'Keyboard Shortcuts',
    description: 'Heading for the keyboard shortcut helper modal',
    id: 'f29a246793',
});
export const KeyboardShortcutHelperModal = React.memo(function KeyboardShortcutHelperModal({ onClose, sections }) {
    const { $t } = useIntl();
    return (_jsxs(Modal, { onClose: onClose, size: "medium", children: [_jsx(Modal.Header, { title: $t(MODAL_TITLE) }), _jsx(Modal.Body, { children: sections.map((section, index) => (_jsx(SectionGroup, { section: section, isFirst: index === 0 }, section.group))) }), _jsx(Modal.Footer, { children: _jsx(BoxPrimitive, { className: "w-full border-super-subtle border-t pt-4", children: _jsx(KeyLegend, {}) }) })] }));
});
//# sourceMappingURL=KeyboardShortcutHelperModal.js.map